import { useRouterState } from '@tanstack/react-router'
import { useCantileverAlerts } from 'features/cantileverAlerts'
import {
  ChatbotAttachmentProvider,
  useChatbotAttachmentsOptional,
} from 'features/chatbot/contexts/ChatbotAttachmentContext'
import { ChatbotFeedbackProvider } from 'features/chatbot/contexts/ChatbotFeedbackContext'
import {
  type ChatSession,
  useChatbotSession,
  useChatbotV2,
} from 'features/chatbot/contexts/ChatbotSessionContext'
import { useChatbotContext } from 'features/chatbot/hooks/useChatbotContext'
import { useContextBuilders } from 'features/chatbot/hooks/useContextBuilders'
import {
  type RCAContextCompat,
  useMessageSender,
} from 'features/chatbot/hooks/useMessageSender'
import { useReconnect } from 'features/chatbot/hooks/useReconnect'
import type { ChatMessage } from 'features/chatbot/types/chat'
import {
  ConversationMessage,
  ThinkingNote,
} from 'features/chatbot/types/conversation'
import { mapBackendMessagesToConversation } from 'features/chatbot/utils/mapBackendMessages'
import { useDocumentAgentStateOptional } from 'features/knowledgeDocumentViewer'
import { useGeckoPermissions } from 'features/permissions'
import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'

interface ChatbotDataContextProps {
  conversation: ConversationMessage[]
  setConversation: React.Dispatch<React.SetStateAction<ConversationMessage[]>>
  isThinking: boolean
  sendChatMessage: (message: string) => void
  sendRCAMessage: (message: string, context?: RCAContextCompat) => void
  sendSupportRequest: (issueType: string, issueDescription: string) => void
  useV2: boolean
  cancelGeneration: () => void
  editingMessageIndex: number | null
  startEditMessage: (index: number) => void
  cancelEditMessage: () => void
  submitEditMessage: (newText: string) => void
  resendMessage: (text: string) => void
  streamingThoughts: ThinkingNote[]
}

export const ChatbotDataContext = createContext<ChatbotDataContextProps>(null!)

interface ChatbotDataLayerInnerProps {
  children: React.ReactNode
  session: ChatSession
}

const ChatbotDataLayerInner: React.FC<ChatbotDataLayerInnerProps> = ({
  children,
  session,
}) => {
  const { updateConversation, updateStreamState, attachEntryId } =
    useChatbotSession()
  const conversation = session.conversation

  const setConversation = useCallback<
    React.Dispatch<React.SetStateAction<ConversationMessage[]>>
  >(
    (updater) => {
      updateConversation(session.id, updater)
    },
    [session.id, updateConversation]
  )

  const appendMessage = useCallback(
    (message: ConversationMessage) => {
      updateConversation(session.id, (prev) => [...prev, message])
    },
    [session.id, updateConversation]
  )

  const [isThinking, setIsThinking] = useState(false)
  const useV2 = useChatbotV2()
  const routerState = useRouterState()
  const search = routerState.location.search
  const { user } = useGeckoPermissions()
  const { sendDelphiSupportEmail } = useCantileverAlerts()
  const contextInfo = useChatbotContext(search)
  const documentAgentState = useDocumentAgentStateOptional()

  const [streamingThoughts, setStreamingThoughts] = useState<ThinkingNote[]>([])

  const [editingMessageIndex, setEditingMessageIndex] = useState<number | null>(
    null
  )

  const { getAttachmentContext, getDocumentViewerContext } =
    useContextBuilders()

  const reconnectTriedRef = useRef<string | null>(null)
  const reconnectRef = useRef<() => void>(() => {})
  const streamRecoveryAttemptsRef = useRef(0)
  const onStreamStarted = useCallback(
    (streamId: string, messageId: string) => {
      // Mark this stream as "ours" so the reconnect-recovery effect
      // below does NOT also open a second connection to it. That
      // effect is meant to recover in-progress streams that survive
      // a page reload, not to re-attach to one we just started
      // ourselves — doing so delivers every SSE event twice and
      // produces duplicate thoughts and aiResponse renders.
      //
      // Important: we key on `streamId` alone, not `entryId:streamId`.
      // The onEvent callback passed to `connectToStream` captures
      // this `onStreamStarted` *at send time*, before
      // `onEntryIdAttached` updates `session.entryId`. Including
      // `entryId` in the key would lock the closure to a stale
      // (null) entryId and the recovery effect — running with the
      // freshly-set entryId — would see a mismatched key and fire
      // anyway. `streamId` is unique per stream, so it's enough.
      reconnectTriedRef.current = streamId
      streamRecoveryAttemptsRef.current = 0
      updateStreamState(session.id, {
        activeStreamId: streamId,
        activeMessageId: messageId,
        lastSeq: 0,
        lastMessageStatus: 'in_progress',
      })
    },
    [session.id, updateStreamState]
  )

  const onStreamSeq = useCallback(
    (seq: number) => {
      if (seq > session.stream.lastSeq) {
        updateStreamState(session.id, { lastSeq: seq })
      }
    },
    [session.id, session.stream.lastSeq, updateStreamState]
  )

  const onStreamComplete = useCallback(
    (status: 'complete' | 'error') => {
      streamRecoveryAttemptsRef.current = 0
      updateStreamState(session.id, {
        activeStreamId: null,
        activeMessageId: null,
        lastMessageStatus: status,
      })
    },
    [session.id, updateStreamState]
  )

  const MAX_STREAM_RECOVERY_ATTEMPTS = 5

  const onStreamInterrupted = useCallback(() => {
    if (streamRecoveryAttemptsRef.current >= MAX_STREAM_RECOVERY_ATTEMPTS) {
      appendMessage({
        type: 'error',
        text: 'Lost connection to the assistant. The response may still finish in the background — reload the conversation to check.',
      })
      setIsThinking(false)
      onStreamComplete('error')
      return
    }
    streamRecoveryAttemptsRef.current += 1
    setIsThinking(true)
    reconnectRef.current()
  }, [appendMessage, onStreamComplete])

  const onEntryIdAttached = useCallback(
    (entryId: string) => {
      if (!session.entryId) attachEntryId(session.id, entryId)
    },
    [session.entryId, session.id, attachEntryId]
  )

  const {
    sendChatMessage,
    sendRCAMessage,
    cancelGeneration,
    handleStreamEvent,
  } = useMessageSender({
    session,
    contextInfo,
    documentAgentState,
    appendMessage,
    setIsThinking,
    getAttachmentContext,
    getDocumentViewerContext,
    onStreamingThoughtsChange: setStreamingThoughts,
    onStreamStarted,
    onStreamSeq,
    onStreamComplete,
    onEntryIdAttached,
    onStreamInterrupted,
  })

  const handleReconnectMessages = useCallback(
    (msgs: ChatMessage[]) => {
      const mapped = mapBackendMessagesToConversation(msgs)
      updateConversation(session.id, mapped)
    },
    [session.id, updateConversation]
  )
  const { reconnect, stopPolling } = useReconnect({
    sessionId: session.entryId,
    streamId: session.stream.activeStreamId,
    lastSeq: session.stream.lastSeq,
    onEvent: handleStreamEvent,
    onMessagesLoaded: handleReconnectMessages,
    onError: (err) => console.warn('[Chatbot reconnect]', err),
    onComplete: () => {
      setIsThinking(false)
      streamRecoveryAttemptsRef.current = 0
      updateStreamState(session.id, {
        activeStreamId: null,
        activeMessageId: null,
        lastMessageStatus: 'complete',
      })
    },
    onDisconnect: onStreamInterrupted,
  })

  useEffect(() => {
    reconnectRef.current = reconnect
  }, [reconnect])

  useEffect(() => {
    const streamKey = session.stream.activeStreamId
    if (
      session.entryId &&
      session.stream.lastMessageStatus === 'in_progress' &&
      streamKey &&
      reconnectTriedRef.current !== streamKey
    ) {
      reconnectTriedRef.current = streamKey
      setIsThinking(true)
      reconnect()
    }
    return () => {
      stopPolling()
    }
  }, [
    session.entryId,
    session.stream.activeStreamId,
    session.stream.lastMessageStatus,
    reconnect,
    stopPolling,
  ])

  const sessionIdRef = useRef<string | null>(session.entryId)
  sessionIdRef.current = session.entryId

  const sendSupportRequest = (issueType: string, issueDescription: string) => {
    if (user?.email !== undefined) {
      sendDelphiSupportEmail(
        user?.email,
        sessionIdRef.current ?? '',
        issueType,
        issueDescription
      )
    }
  }

  const startEditMessage = (index: number) => {
    setEditingMessageIndex(index)
  }

  const cancelEditMessage = () => {
    setEditingMessageIndex(null)
  }

  const submitEditMessage = (newText: string) => {
    if (editingMessageIndex === null) return

    cancelGeneration()

    setEditingMessageIndex(null)

    sendRCAMessage(newText)
  }

  const resendMessage = useCallback(
    (text: string) => {
      sendRCAMessage(text)
    },
    [sendRCAMessage]
  )

  return (
    <ChatbotDataContext.Provider
      value={{
        conversation,
        setConversation,
        isThinking,
        sendChatMessage,
        sendRCAMessage,
        sendSupportRequest,
        useV2,
        cancelGeneration,
        editingMessageIndex,
        startEditMessage,
        cancelEditMessage,
        submitEditMessage,
        resendMessage,
        streamingThoughts,
      }}>
      <ChatbotFeedbackProvider
        conversation={conversation}
        externalEntryId={session.entryId ?? undefined}
        externalSessionId={session.sessionId}
        sessionName={session.name}
        siteId={contextInfo.siteId}
        organizationId={contextInfo.organizationIds?.[0]}>
        {children}
      </ChatbotFeedbackProvider>
    </ChatbotDataContext.Provider>
  )
}

export interface ChatbotDataLayerProps {
  children: React.ReactNode
  session: ChatSession
}

export const ChatbotDataLayer: React.FC<ChatbotDataLayerProps> = ({
  children,
  session,
}) => {
  const existingAttachmentContext = useChatbotAttachmentsOptional()

  const inner = (
    <ChatbotDataLayerInner session={session}>{children}</ChatbotDataLayerInner>
  )

  if (existingAttachmentContext === undefined) {
    return <ChatbotAttachmentProvider>{inner}</ChatbotAttachmentProvider>
  }

  return inner
}
